import { useCallback, useEffect, useState } from 'react';
import { Archive, FileUp, KeyRound, ListVideo, Plus, Save, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  type ApiError,
  type ContentType,
  type FulfillmentType,
  type ProductStatus,
} from '../../lib/api';
import { formatPrice, subjectIcon } from '../../lib/format';
import {
  adminApi,
  type AdminCategory,
  type AdminProductDetail,
  type AdminProductListItem,
  type AdminSubject,
  type ProductCreate,
} from '../../lib/admin';

const CONTENT_TYPES: { value: ContentType; label: string }[] = [
  { value: 'PDF', label: 'PDF materiál' },
  { value: 'VIDEO', label: 'Videokurz' },
  { value: 'APP', label: 'Aplikace' },
];
const FULFILLMENTS: { value: FulfillmentType; label: string }[] = [
  { value: 'DOWNLOAD', label: 'Ke stažení' },
  { value: 'EMAIL', label: 'Přílohou e-mailem' },
  { value: 'STREAM', label: 'Streamované video' },
  { value: 'LICENSE_KEY', label: 'Licenční klíč' },
];

const STATUS_LABEL: Record<ProductStatus, string> = {
  DRAFT: 'Koncept',
  PUBLISHED: 'Zveřejněno',
  ARCHIVED: 'Archivováno',
};

function statusClasses(status: ProductStatus): string {
  if (status === 'PUBLISHED') return 'bg-success/10 text-success';
  if (status === 'ARCHIVED') return 'bg-error/10 text-error';
  return 'bg-warning/10 text-warning';
}

/* Formulář nového materiálu */

function labelledInput(label: string, node: React.ReactNode, hint?: string): React.ReactNode {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-ink">{label}</span>
      {node}
      {hint && <span className="mt-0.5 block text-xs text-ink-soft">{hint}</span>}
    </label>
  );
}

const inputClass =
  'mt-1 h-11 w-full rounded-xl border border-line bg-paper px-3 text-ink outline-none focus:border-board';
const selectClass = inputClass;

const bySort = (a: { sort_order: number; name: string }, b: { sort_order: number; name: string }) =>
  a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'cs');

/** Výběr předmětu z číselníku (nahrazuje hardcode SUBJECTS). */
function SubjectSelect({
  subjects,
  value,
  onChange,
}: {
  subjects: AdminSubject[];
  value: string;
  onChange: (slug: string) => void;
}) {
  return (
    <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value)}>
      {subjects.length === 0 && <option value="">žádný předmět</option>}
      {[...subjects].sort(bySort).map((s) => (
        <option key={s.id} value={s.slug}>
          {s.name}
        </option>
      ))}
    </select>
  );
}

/** Zaškrtávací strom kategorií (kořen tučně, děti odsazené). Materiál smí být ve více. */
function CategoryPicker({
  categories,
  selected,
  onToggle,
}: {
  categories: AdminCategory[];
  selected: string[];
  onToggle: (slug: string) => void;
}) {
  const roots = categories.filter((c) => c.parent_id === null).sort(bySort);

  if (categories.length === 0) {
    return (
      <p className="mt-1 rounded-xl border border-dashed border-line bg-paper2/60 px-3 py-2 text-sm text-ink-soft">
        Zatím žádné kategorie. Založ je v sekci Kategorie a předměty.
      </p>
    );
  }

  const Row = ({ cat, child }: { cat: AdminCategory; child: boolean }) => {
    const Icon = subjectIcon(cat.icon);
    const on = selected.includes(cat.slug);
    return (
      <label
        className={`flex min-h-[40px] cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 transition hover:bg-paper2 ${
          child ? 'ml-5' : ''
        }`}
      >
        <input
          type="checkbox"
          className="h-4 w-4 accent-board"
          checked={on}
          onChange={() => onToggle(cat.slug)}
        />
        <Icon className="h-4 w-4 flex-none text-board" strokeWidth={2} />
        <span className={child ? 'text-sm' : 'text-sm font-semibold'}>{cat.name}</span>
      </label>
    );
  };

  return (
    <div className="mt-1 max-h-56 space-y-0.5 overflow-y-auto rounded-xl border border-line bg-paper p-2">
      {roots.map((root) => (
        <div key={root.id}>
          <Row cat={root} child={false} />
          {categories
            .filter((c) => c.parent_id === root.id)
            .sort(bySort)
            .map((kid) => (
              <Row key={kid.id} cat={kid} child />
            ))}
        </div>
      ))}
    </div>
  );
}

function CreateForm({
  subjects,
  categories,
  onCreated,
  onCancel,
}: {
  subjects: AdminSubject[];
  categories: AdminCategory[];
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<ProductCreate>({
    slug: '',
    title: '',
    price_czk: 0,
    subject: [...subjects].sort(bySort)[0]?.slug ?? '',
    content_type: 'PDF',
    fulfillment_type: 'DOWNLOAD',
    description_md: '',
    category_slugs: [],
  });
  const [pending, setPending] = useState(false);

  const toggleCategory = (slug: string) =>
    setForm((f) => {
      const cur = f.category_slugs ?? [];
      return {
        ...f,
        category_slugs: cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug],
      };
    });

  const set = <K extends keyof ProductCreate>(key: K, value: ProductCreate[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending) return;
    setPending(true);
    try {
      await adminApi.createProduct(form);
      toast.success('Materiál vytvořen (koncept)');
      onCreated();
    } catch (err) {
      toast.error('Nepodařilo se vytvořit', { description: (err as ApiError)?.message });
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-3 rounded-card border border-line bg-card p-5 shadow-card"
    >
      <h2 className="font-serif text-xl">Nový materiál</h2>
      {labelledInput(
        'Název',
        <input
          className={inputClass}
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          required
        />,
      )}
      {labelledInput(
        'Webová adresa (slug)',
        <input
          className={inputClass}
          value={form.slug}
          onChange={(e) => set('slug', e.target.value)}
          placeholder="napr-rovnice-krok-za-krokem"
          pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
          required
        />,
        'Jen malá písmena bez diakritiky, číslice a pomlčky.',
      )}
      <div className="grid grid-cols-2 gap-3">
        {labelledInput(
          'Cena (Kč)',
          <input
            type="number"
            min={0}
            className={inputClass}
            value={form.price_czk}
            onChange={(e) => set('price_czk', Number(e.target.value))}
            required
          />,
        )}
        {labelledInput(
          'Předmět',
          <SubjectSelect
            subjects={subjects}
            value={form.subject}
            onChange={(slug) => set('subject', slug)}
          />,
          'Hlavní štítek. Určuje ikonu a barvu náhledu.',
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {labelledInput(
          'Druh obsahu',
          <select
            className={selectClass}
            value={form.content_type}
            onChange={(e) => set('content_type', e.target.value as ContentType)}
          >
            {CONTENT_TYPES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>,
        )}
        {labelledInput(
          'Způsob doručení',
          <select
            className={selectClass}
            value={form.fulfillment_type}
            onChange={(e) => set('fulfillment_type', e.target.value as FulfillmentType)}
          >
            {FULFILLMENTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>,
        )}
      </div>
      {labelledInput(
        'Krátký popis',
        <textarea
          className="mt-1 min-h-[80px] w-full rounded-xl border border-line bg-paper px-3 py-2 text-ink outline-none focus:border-board"
          value={form.description_md}
          onChange={(e) => set('description_md', e.target.value)}
        />,
      )}
      {labelledInput(
        'Kategorie',
        <CategoryPicker
          categories={categories}
          selected={form.category_slugs ?? []}
          onToggle={toggleCategory}
        />,
        'Poličky ve výkladu, materiál smí být ve více.',
      )}
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={pending}
          className="inline-flex h-11 items-center gap-2 rounded-xl bg-board px-4 font-semibold text-chalk transition hover:bg-board-deep disabled:opacity-50"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          Vytvořit
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex h-11 items-center rounded-xl border border-line bg-card px-4 font-semibold text-ink-soft transition hover:border-board"
        >
          Zrušit
        </button>
      </div>
    </form>
  );
}

/* Editor materiálu */

function AssetsSection({
  product,
  onChanged,
}: {
  product: AdminProductDetail;
  onChanged: () => void;
}) {
  const [kind, setKind] = useState<'FULL' | 'SAMPLE' | 'GALLERY'>('FULL');
  const [busy, setBusy] = useState(false);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      await adminApi.uploadAsset(product.id, kind, file);
      toast.success('Soubor nahrán');
      onChanged();
    } catch (err) {
      toast.error('Nahrání selhalo', { description: (err as ApiError)?.message });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await adminApi.deleteAsset(id);
      toast.success('Soubor smazán');
      onChanged();
    } catch (err) {
      toast.error('Nepodařilo se smazat', { description: (err as ApiError)?.message });
    }
  };

  return (
    <section>
      <h3 className="flex items-center gap-2 font-semibold text-ink">
        <FileUp className="h-4 w-4 text-board" strokeWidth={2} /> Soubory
      </h3>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select
          className="h-11 rounded-xl border border-line bg-paper px-3 text-sm outline-none focus:border-board"
          value={kind}
          onChange={(e) => setKind(e.target.value as typeof kind)}
        >
          <option value="FULL">Plný obsah</option>
          <option value="SAMPLE">Ukázka</option>
          <option value="GALLERY">Náhled do galerie</option>
        </select>
        <label className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-xl bg-board px-4 text-sm font-semibold text-chalk transition hover:bg-board-deep">
          <FileUp className="h-4 w-4" strokeWidth={2} />
          {busy ? 'Nahrávám…' : 'Nahrát soubor'}
          <input
            type="file"
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      <ul className="mt-2 space-y-1.5">
        {product.assets.length === 0 && (
          <li className="text-sm text-ink-soft">Zatím žádné soubory.</li>
        )}
        {product.assets.map((a) => (
          <li
            key={a.id}
            className="flex items-center justify-between gap-2 rounded-lg border border-line bg-paper px-3 py-2 text-sm"
          >
            <span className="min-w-0 truncate">
              <span className="rounded bg-paper2 px-1.5 py-0.5 text-[11px] font-semibold text-ink-soft">
                {a.kind === 'FULL' ? 'Plný' : a.kind === 'SAMPLE' ? 'Ukázka' : 'Náhled'}
              </span>{' '}
              {a.filename}
            </span>
            <button
              type="button"
              onClick={() => void remove(a.id)}
              aria-label="Smazat soubor"
              className="grid h-8 w-8 flex-none place-items-center rounded-lg text-error transition hover:bg-error/5"
            >
              <Trash2 className="h-4 w-4" strokeWidth={2} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LessonsSection({
  product,
  onChanged,
}: {
  product: AdminProductDetail;
  onChanged: () => void;
}) {
  const [title, setTitle] = useState('');
  const [minutes, setMinutes] = useState(5);

  const add = async () => {
    if (!title.trim()) return;
    try {
      await adminApi.addLesson(product.id, { title: title.trim(), duration_s: minutes * 60 });
      toast.success('Lekce přidána');
      setTitle('');
      onChanged();
    } catch (err) {
      toast.error('Nepodařilo se přidat lekci', { description: (err as ApiError)?.message });
    }
  };

  const remove = async (id: number) => {
    try {
      await adminApi.deleteLesson(id);
      onChanged();
    } catch (err) {
      toast.error('Nepodařilo se smazat', { description: (err as ApiError)?.message });
    }
  };

  return (
    <section>
      <h3 className="flex items-center gap-2 font-semibold text-ink">
        <ListVideo className="h-4 w-4 text-board" strokeWidth={2} /> Lekce videa
      </h3>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <input
          className="h-11 grow rounded-xl border border-line bg-paper px-3 text-sm outline-none focus:border-board"
          placeholder="Název lekce"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <input
          type="number"
          min={0}
          className="h-11 w-20 rounded-xl border border-line bg-paper px-3 text-sm outline-none focus:border-board"
          value={minutes}
          onChange={(e) => setMinutes(Number(e.target.value))}
          aria-label="Délka v minutách"
        />
        <button
          type="button"
          onClick={() => void add()}
          className="inline-flex h-11 items-center gap-1.5 rounded-xl bg-board px-3 text-sm font-semibold text-chalk transition hover:bg-board-deep"
        >
          <Plus className="h-4 w-4" strokeWidth={2} /> Přidat
        </button>
      </div>
      <ul className="mt-2 space-y-1.5">
        {product.lessons.map((l) => (
          <li
            key={l.id}
            className="flex items-center justify-between rounded-lg border border-line bg-paper px-3 py-2 text-sm"
          >
            <span>
              {l.order_index}. {l.title}
              {l.is_sample && <span className="ml-2 text-xs text-accent">ukázka</span>}
            </span>
            <button
              type="button"
              onClick={() => void remove(l.id)}
              aria-label="Smazat lekci"
              className="grid h-8 w-8 place-items-center rounded-lg text-error transition hover:bg-error/5"
            >
              <Trash2 className="h-4 w-4" strokeWidth={2} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LicenseSection({
  product,
  onChanged,
}: {
  product: AdminProductDetail;
  onChanged: () => void;
}) {
  const [text, setText] = useState('');
  const free = product.license_keys.filter((k) => k.state === 'FREE').length;

  const add = async () => {
    const keys = text
      .split(/[\n,]+/)
      .map((k) => k.trim())
      .filter(Boolean);
    if (keys.length === 0) return;
    try {
      const res = await adminApi.addLicenseKeys(product.id, keys);
      toast.success(`Přidáno ${res.added}, přeskočeno ${res.skipped}`);
      setText('');
      onChanged();
    } catch (err) {
      toast.error('Nepodařilo se přidat klíče', { description: (err as ApiError)?.message });
    }
  };

  return (
    <section>
      <h3 className="flex items-center gap-2 font-semibold text-ink">
        <KeyRound className="h-4 w-4 text-board" strokeWidth={2} /> Licenční klíče
        <span className="text-sm font-normal text-ink-soft">
          ({free} volných z {product.license_keys.length})
        </span>
      </h3>
      <textarea
        className="mt-2 min-h-[70px] w-full rounded-xl border border-line bg-paper px-3 py-2 text-sm outline-none focus:border-board"
        placeholder="Jeden klíč na řádek…"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <button
        type="button"
        onClick={() => void add()}
        className="mt-2 inline-flex h-11 items-center gap-2 rounded-xl bg-board px-4 text-sm font-semibold text-chalk transition hover:bg-board-deep"
      >
        <Plus className="h-4 w-4" strokeWidth={2} /> Přidat klíče
      </button>
    </section>
  );
}

type ProductDraft = {
  title: string;
  price_czk: number;
  subtitle: string;
  grade: string;
  description_md: string;
  sample_note: string;
  subject: string;
  category_slugs: string[];
};

function Editor({
  productId,
  subjects,
  categories,
  onChanged,
}: {
  productId: number;
  subjects: AdminSubject[];
  categories: AdminCategory[];
  onChanged: () => void;
}) {
  const [product, setProduct] = useState<AdminProductDetail | null>(null);
  const [draft, setDraft] = useState<ProductDraft>();
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const p = await adminApi.getProduct(productId);
      setProduct(p);
      setDraft({
        title: p.title,
        price_czk: p.price_czk,
        subtitle: p.subtitle ?? '',
        grade: p.grade ?? '',
        description_md: p.description_md,
        sample_note: p.sample_note ?? '',
        subject: p.subject.slug,
        category_slugs: p.category_slugs,
      });
    } catch (err) {
      toast.error('Detail se nepodařilo načíst', { description: (err as ApiError)?.message });
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = async () => {
    await load();
    onChanged();
  };

  if (!product || !draft) {
    return <div className="h-64 rounded-card bg-paper2 motion-safe:animate-pulse" />;
  }

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.updateProduct(product.id, {
        title: draft.title,
        price_czk: draft.price_czk,
        subtitle: draft.subtitle || null,
        grade: draft.grade || null,
        description_md: draft.description_md,
        sample_note: draft.sample_note || null,
        subject: draft.subject,
        category_slugs: draft.category_slugs,
      });
      toast.success('Uloženo');
      await refresh();
    } catch (err) {
      toast.error('Uložení selhalo', { description: (err as ApiError)?.message });
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status: ProductStatus) => {
    try {
      await adminApi.updateProduct(product.id, { status });
      toast.success(
        status === 'PUBLISHED' ? 'Zveřejněno' : status === 'ARCHIVED' ? 'Archivováno' : 'Uloženo',
      );
      await refresh();
    } catch (err) {
      toast.error('Změna stavu selhala', { description: (err as ApiError)?.message });
    }
  };

  return (
    <div className="space-y-5 rounded-card border border-line bg-card p-5 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl leading-tight">{product.title}</h2>
          <p className="mt-0.5 font-mono text-xs text-ink-soft">{product.slug}</p>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusClasses(product.status)}`}
        >
          {STATUS_LABEL[product.status]}
        </span>
      </div>

      {/* Publikace */}
      <div className="flex flex-wrap gap-2">
        {product.status !== 'PUBLISHED' && (
          <button
            type="button"
            onClick={() => void setStatus('PUBLISHED')}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-success px-4 text-sm font-semibold text-white transition hover:brightness-95"
          >
            <Send className="h-4 w-4" strokeWidth={2} /> Zveřejnit
          </button>
        )}
        {product.status !== 'ARCHIVED' && (
          <button
            type="button"
            onClick={() => void setStatus('ARCHIVED')}
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-line bg-card px-4 text-sm font-semibold text-ink-soft transition hover:border-board"
          >
            <Archive className="h-4 w-4" strokeWidth={2} /> Archivovat
          </button>
        )}
        {product.status === 'ARCHIVED' && (
          <button
            type="button"
            onClick={() => void setStatus('DRAFT')}
            className="inline-flex h-10 items-center rounded-xl border border-line bg-card px-4 text-sm font-semibold text-ink-soft transition hover:border-board"
          >
            Vrátit do konceptů
          </button>
        )}
      </div>

      {/* Základní pole */}
      <div className="space-y-3">
        {labelledInput(
          'Název',
          <input
            className={inputClass}
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          />,
        )}
        <div className="grid grid-cols-2 gap-3">
          {labelledInput(
            'Cena (Kč)',
            <input
              type="number"
              min={0}
              className={inputClass}
              value={draft.price_czk}
              onChange={(e) => setDraft({ ...draft, price_czk: Number(e.target.value) })}
            />,
          )}
          {labelledInput(
            'Ročník',
            <input
              className={inputClass}
              value={draft.grade}
              onChange={(e) => setDraft({ ...draft, grade: e.target.value })}
              placeholder="např. 9. třída"
            />,
          )}
        </div>
        {labelledInput(
          'Předmět',
          <SubjectSelect
            subjects={subjects}
            value={draft.subject}
            onChange={(slug) => setDraft({ ...draft, subject: slug })}
          />,
          'Hlavní štítek. Určuje ikonu a barvu náhledu.',
        )}
        {labelledInput(
          'Podtitulek',
          <input
            className={inputClass}
            value={draft.subtitle}
            onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
          />,
        )}
        {labelledInput(
          'Popis',
          <textarea
            className="mt-1 min-h-[90px] w-full rounded-xl border border-line bg-paper px-3 py-2 text-ink outline-none focus:border-board"
            value={draft.description_md}
            onChange={(e) => setDraft({ ...draft, description_md: e.target.value })}
          />,
        )}
        {labelledInput(
          'Poznámka k ukázce',
          <input
            className={inputClass}
            value={draft.sample_note}
            onChange={(e) => setDraft({ ...draft, sample_note: e.target.value })}
          />,
        )}
        {labelledInput(
          'Kategorie',
          <CategoryPicker
            categories={categories}
            selected={draft.category_slugs}
            onToggle={(slug) =>
              setDraft({
                ...draft,
                category_slugs: draft.category_slugs.includes(slug)
                  ? draft.category_slugs.filter((s) => s !== slug)
                  : [...draft.category_slugs, slug],
              })
            }
          />,
          'Poličky ve výkladu, materiál smí být ve více.',
        )}
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="inline-flex h-11 items-center gap-2 rounded-xl bg-board px-4 font-semibold text-chalk transition hover:bg-board-deep disabled:opacity-50"
        >
          <Save className="h-4 w-4" strokeWidth={2} /> Uložit změny
        </button>
      </div>

      <div className="border-t border-line pt-4">
        <AssetsSection product={product} onChanged={() => void refresh()} />
      </div>
      {product.content_type === 'VIDEO' && (
        <div className="border-t border-line pt-4">
          <LessonsSection product={product} onChanged={() => void refresh()} />
        </div>
      )}
      {product.fulfillment_type === 'LICENSE_KEY' && (
        <div className="border-t border-line pt-4">
          <LicenseSection product={product} onChanged={() => void refresh()} />
        </div>
      )}
    </div>
  );
}

/* Stránka */

const FILTERS: { value: ProductStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Vše' },
  { value: 'DRAFT', label: 'Koncepty' },
  { value: 'PUBLISHED', label: 'Zveřejněné' },
  { value: 'ARCHIVED', label: 'Archiv' },
];

export default function AdminProducts() {
  const [filter, setFilter] = useState<ProductStatus | 'ALL'>('ALL');
  const [products, setProducts] = useState<AdminProductListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [subjects, setSubjects] = useState<AdminSubject[]>([]);
  const [categories, setCategories] = useState<AdminCategory[]>([]);

  // Číselník předmětů a strom kategorií pro formulář produktu.
  useEffect(() => {
    void adminApi
      .listSubjects()
      .then(setSubjects)
      .catch(() => undefined);
    void adminApi
      .listCategories()
      .then(setCategories)
      .catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProducts(await adminApi.listProducts({ status: filter === 'ALL' ? undefined : filter }));
    } catch (err) {
      toast.error('Materiály se nepodařilo načíst', { description: (err as ApiError)?.message });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="font-serif text-2xl">Materiály</h1>
        <button
          type="button"
          onClick={() => {
            setCreating(true);
            setSelectedId(null);
          }}
          className="inline-flex h-10 items-center gap-2 rounded-xl bg-board px-4 text-sm font-semibold text-chalk transition hover:bg-board-deep"
        >
          <Plus className="h-4 w-4" strokeWidth={2} /> Přidat materiál
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={`h-9 rounded-full px-3.5 text-sm font-semibold transition ${
              filter === f.value
                ? 'bg-board text-chalk'
                : 'border border-line bg-card text-ink-soft'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
        {/* Seznam */}
        <div className="space-y-2">
          {loading ? (
            <div className="h-40 rounded-card bg-paper2 motion-safe:animate-pulse" />
          ) : products.length === 0 ? (
            <p className="rounded-card border border-line bg-card p-6 text-center text-ink-soft">
              Žádné materiály.
            </p>
          ) : (
            products.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setSelectedId(p.id);
                  setCreating(false);
                }}
                className={`flex w-full items-center gap-3 rounded-card border bg-card p-3.5 text-left shadow-card transition hover:border-board ${
                  selectedId === p.id ? 'border-board' : 'border-line'
                }`}
              >
                <div className="min-w-0 grow">
                  <p className="truncate font-semibold text-ink">{p.title}</p>
                  <p className="mt-0.5 text-sm text-ink-soft">
                    {formatPrice(p.price_czk)} · {p.asset_count} souborů
                    {p.fulfillment_type === 'LICENSE_KEY' && ` · ${p.license_free} volných klíčů`}
                    {p.content_type === 'VIDEO' && ` · ${p.lesson_count} lekcí`}
                  </p>
                </div>
                <span
                  className={`flex-none rounded-full px-2.5 py-1 text-xs font-semibold ${statusClasses(p.status)}`}
                >
                  {STATUS_LABEL[p.status]}
                </span>
              </button>
            ))
          )}
        </div>

        {/* Detail / vytvoření */}
        <div>
          {creating ? (
            <CreateForm
              subjects={subjects}
              categories={categories}
              onCreated={() => {
                setCreating(false);
                void load();
              }}
              onCancel={() => setCreating(false)}
            />
          ) : selectedId ? (
            <Editor
              productId={selectedId}
              subjects={subjects}
              categories={categories}
              onChanged={() => void load()}
            />
          ) : (
            <p className="rounded-card border border-dashed border-line bg-card/60 p-6 text-center text-sm text-ink-soft">
              Vyberte materiál, nebo přidejte nový.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
